
<template>
  <Page>
    <div class="space-y-2">
      <!-- Page Header Card -->
      <section id="page-header" class="bg-background-card rounded-lg shadow-card overflow-hidden p-4 flex items-center justify-between">
        <div class="flex items-center">
          <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mr-4">
            <i class="fa-solid fa-folder-open text-xl"></i>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-primary">Project Management</h1>
            <p class="text-secondary">Create, manage and configure projects across your organization</p>
          </div>
        </div>
        <button id="new-project-btn"
          class="bg-accent text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-accent/90 transition-colors flex items-center"
          @click="onNewProject"
          :disabled="actionLoading"
        >
          <i class="fa-solid fa-plus mr-2"></i>
          New Project
        </button>
      </section>

      <!-- Project List Card -->
      <ProjectListCard
        :projects="projects"
        :is-loading="isLoading"
        :error="error"
        :active-tab="activeTab"
        :current-page="page"
        :total-items="total"
        :page-size="pageSize"
        :search-value="search"
        :status-counts="statusCounts"
        :is-action-loading="actionLoading"
        @update:active-tab="onTabChange"
        @update:search-value="onSearchUpdate"
        @search="onSearch"
        @page-change="onPageChange"
        @project-action="onProjectAction"
        @more-action="onMoreAction"
        @retry="onRetry"
      />
    </div>

      <!-- More Actions Overlay Panel -->
      <Popover ref="moreActionsOverlay" class="w-48">
        <div class="py-2" v-if="selectedProjectForMore">
          <Button
            text
            class="w-full justify-start text-left p-3 hover:bg-background"
            @click="() => { if (selectedProjectForMore) { handleRenameProject(selectedProjectForMore); moreActionsOverlay.hide(); } }"
          >
            <i class="fa-solid fa-edit mr-3"></i>
            Rename
          </Button>
          <Button
            text
            class="w-full justify-start text-left p-3 hover:bg-background"
            @click="handleArchiveProject(selectedProjectForMore); moreActionsOverlay.hide()"
            v-if="selectedProjectForMore.status !== ProjectStatus.Archived"
          >
            <i class="fa-solid fa-archive mr-3"></i>
            Archive
          </Button>
          <Button
            text
            class="w-full justify-start text-left p-3 hover:bg-background text-success"
            @click="handleRestoreProject(selectedProjectForMore); moreActionsOverlay.hide()"
            v-if="selectedProjectForMore.status === ProjectStatus.Archived"
          >
            <i class="fa-solid fa-undo mr-3"></i>
            Restore
          </Button>
          <Button
            text
            class="w-full justify-start text-left p-3 hover:bg-background text-error"
            @click="handleDeleteProject(selectedProjectForMore); moreActionsOverlay.hide()"
          >
            <i class="fa-solid fa-trash mr-3"></i>
            Delete
          </Button>
        </div>
      </Popover>

      <!-- Rename Project Dialog -->
      <RenameProjectDialog
        :visible="renameDialogVisible"
        :current-name="projectToRename?.name || ''"
        @confirm="handleRenameConfirm"
        @cancel="handleRenameCancel"
      />

  <!-- Create Project moved to its own page at /project-management/create -->
  </Page>
</template>


<script setup lang="ts">
import Page from '@/shared/components/layouts/Page.vue';
import ProjectListCard from '../components/ProjectListCard.vue';
import RenameProjectDialog from '../components/RenameProjectDialog.vue';
import Button from 'primevue/button';
import { useProjectPage } from '../composables/useProjectPage';
import { ProjectStatus } from '@/core/types/models/Project';
import type { ProjectActionType } from '../components/ProjectActions.vue';

definePage({
  meta: {
    requiresProject: false,
  },
});

// Use composable which contains page logic and handlers.
const {
  // state
  search,
  activeTab,
  moreActionsOverlay,
  selectedProjectForMore,
  renameDialogVisible,
  projectToRename,

  // computed
  isLoading,
  projects,
  error,
  page,
  pageSize,
  total,
  statusCounts,
  actionLoading,

  // methods
  onSearchUpdate,
  onSearch,
  onTabChange,
  onPageChange,
  handleViewProject,
  handleUseTemplate,
  handleMoreActions,
  handleRenameProject,
  handleRenameConfirm: originalHandleRenameConfirm,
  handleRenameCancel,
  handleArchiveProject,
  handleDeleteProject,
  handleRestoreProject,
  onMoreAction,
  onRetry,
  onNewProject
} = useProjectPage();

// Wrapper method to handle the new name from the dialog
function handleRenameConfirm(newName: string): void {
  originalHandleRenameConfirm(newName);
}

function onProjectAction(actionType: ProjectActionType, project: any) {
  // Route actions through composable handlers
  switch (actionType) {
    case 'view':
      handleViewProject(project);
      break;
    case 'use-template':
      handleUseTemplate(project);
      break;
    case 'more':
      handleMoreActions(project);
      break;
  }
}
</script>